import { useCallback, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { toast } from '@/components'
import { isTRPCClientError } from '@/utils/trpc'

export type DeleteToastMode = 'both' | 'error' | 'success' | 'none'

type DeleteStatus = 'idle' | 'deleting' | 'deleted' | 'error'

export const useDelete = ({ toastMode = 'both' }: { toastMode?: DeleteToastMode } = {}) => {
    const [deleteStatus, setDeleteStatus] = useState<DeleteStatus>('idle')

    const { t } = useTranslation()

    const toastRef = useRef<string | null>(null)

    const submitDelete = useCallback(
        async (deleteFunction: () => Promise<string | void | boolean>) => {
            if (toastRef.current) {
                toast.remove(toastRef.current)
                toastRef.current = null
            }

            try {
                setDeleteStatus('deleting')

                const result = await deleteFunction()
                if (result === false) {
                    return false
                }

                setDeleteStatus('deleted')

                if (toastMode === 'success' || toastMode === 'both') {
                    toastRef.current = toast.success(
                        result && typeof result === 'string' ? result : t('components.form.deleteSuccess')
                    )
                }

                return true
            } catch (e) {
                let message = t('components.form.deleteError')

                if (isTRPCClientError(e)) {
                    message = e.message
                }

                setDeleteStatus('error')

                if (toastMode === 'error' || toastMode === 'both') {
                    toastRef.current = toast.error(message)
                }

                return false
            }
        },
        [t, toastMode]
    )

    return { submitDelete, deleteStatus, isDeleting: deleteStatus === 'deleting' }
}
